import type { Faculty } from '@/lib/api/auth'

/**
 * Le compte à rebours vers le concours — RG-1.11quater.
 *
 * « Si elle est renseignée, l'application affiche un compte à rebours et
 * resserre le rythme de révision à l'approche. » Le resserrement du rythme
 * appartient au moteur du Fil (RG-2.10quinquies, lot L2) ; ici, on montre le
 * chiffre.
 *
 * Une date INCONNUE ne s'invente pas : la faculté qui n'en porte pas
 * n'affiche rien. Un compte à rebours faux ferait réviser au mauvais rythme.
 */
export function joursAvant(date: string, aujourdhui = new Date()): number {
  const cible = new Date(`${date}T00:00:00Z`)
  const debutDuJour = new Date(
    Date.UTC(aujourdhui.getUTCFullYear(), aujourdhui.getUTCMonth(), aujourdhui.getUTCDate()),
  )

  return Math.round((cible.getTime() - debutDuJour.getTime()) / 86_400_000)
}

/** La formulation, en français, pour un nombre de jours donné. */
export function libelle(jours: number): string {
  if (jours < 0) return 'Le concours est passé'
  if (jours === 0) return 'C’est aujourd’hui'
  if (jours === 1) return 'Demain'

  return `Dans ${String(jours)} jours`
}

interface Props {
  faculties: Faculty[]
  /** Injectable pour les tests ; sinon la date du jour. */
  maintenant?: Date
}

export function Countdown({ faculties, maintenant }: Props) {
  const prochains = faculties
    .filter((f) => f.exam?.next_exam_date != null)
    .map((f) => ({
      faculte: f,
      date: f.exam?.next_exam_date ?? '',
      jours: joursAvant(f.exam?.next_exam_date ?? '', maintenant),
    }))
    .filter((e) => e.jours >= 0)
    .sort((a, b) => a.jours - b.jours)

  if (prochains.length === 0) return null

  return (
    <section
      className="flex flex-col gap-1 rounded-2xl p-4"
      style={{ background: 'var(--surface-2)' }}
    >
      <h2 className="text-[13px] font-bold" style={{ color: 'var(--texte-2)' }}>
        Prochain concours
      </h2>

      <ul className="flex flex-col gap-2">
        {prochains.map(({ faculte, date, jours }) => (
          <li key={faculte.slug} className="flex items-baseline justify-between gap-3">
            <span className="text-[15px] font-bold">{faculte.short_name}</span>
            <span className="flex flex-col items-end">
              <span
                className="text-[17px] font-extrabold"
                style={{ color: jours <= 30 ? 'var(--faux)' : 'var(--texte-1)' }}
              >
                {libelle(jours)}
              </span>
              {/* RG-D2 — l'urgence n'est jamais portée par la seule couleur :
                  la date reste lisible, et le mot « Dans N jours » aussi. */}
              <time className="text-[13px]" style={{ color: 'var(--texte-2)' }} dateTime={date}>
                {new Date(`${date}T00:00:00Z`).toLocaleDateString('fr', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  timeZone: 'UTC',
                })}
              </time>
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}
